import { ref } from 'vue';
import { parseErrorPayload } from '@lib/api-error';

// Must match Api.Domain/Helpers/Exceptions/ErrorCodes.cs:BomConnectionsWouldBeDropped
const BOM_CONNECTIONS_WOULD_BE_DROPPED = 4001;

type PendingRequest = {
  message: string;
  resolve: (confirmed: boolean) => void;
};

// Module-level queue. Concurrent requests get queued so a second in-flight call
// can't overwrite the first's resolver — the modal shows them one at a time.
const pending: PendingRequest[] = [];
const isOpen = ref(false);
const message = ref('');

function showHead() {
  const head = pending[0];
  if (!head) {
    isOpen.value = false;
    message.value = '';
    return;
  }
  message.value = head.message;
  isOpen.value = true;
}

export const bomDataLossModal = {
  isOpen,
  message,

  resolve(confirmed: boolean) {
    const current = pending.shift();
    current?.resolve(confirmed);
    showHead();
  },
};

function request(msg: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    pending.push({ message: msg, resolve });
    if (pending.length === 1) showHead();
  });
}

function extractBomDataLossMessage(err: unknown): string | null {
  const payload = parseErrorPayload(err);
  if (payload?.errorCode !== BOM_CONNECTIONS_WOULD_BE_DROPPED) return null;
  return (
    payload?.message ?? 'Removing this season would drop BOM connections that have no matching slot in the base BOM.'
  );
}

// Run an API call that may trigger a BOM-data-loss warning. The callback receives a
// `suppressBomDropWarning` flag which it must thread to the backend. On the warning,
// opens a confirm modal; if the user proceeds, retries with the flag set. Returns the
// call result, or null if the user cancels.
export async function runWithBomDataLossConfirm<T>(
  call: (suppressBomDropWarning: boolean) => Promise<T>
): Promise<T | null> {
  try {
    return await call(false);
  } catch (err) {
    const msg = extractBomDataLossMessage(err);
    if (msg === null) throw err;

    const confirmed = await request(msg);
    if (!confirmed) return null;

    return await call(true);
  }
}
